import { useMutation } from "@apollo/client";

import { UPDATE_EVENT_FEEDBACK } from "../../../../rest/mutations";
import { GET_EVENTS_BY_CUSTOMER } from "../../../../rest/queries";
import {
  EventsApiResponse,
  UpdateEventFeedbackInput,
  UpdateEventResponse,
} from "../../../../common/types/events/api";
import {
  errorNotification,
  successNotification,
} from "../../../../common/variables/notification";

type GetEventsByCustomerResponse = {
  events: EventsApiResponse;
};

export const useUpdateEventFeedback = () => {
  const [updateMutation, { loading, error, data }] = useMutation<
    { updateEventFeedback: UpdateEventResponse },
    { input: UpdateEventFeedbackInput }
  >(UPDATE_EVENT_FEEDBACK);

  const updateEventFeedback = async (input: UpdateEventFeedbackInput) => {
    try {
      const response = await updateMutation({
        variables: { input },
        context: {
          headers: {
            "Content-Type": "application/json",
          },
        },
        onCompleted: () => {
          successNotification("Event feedback updated successfully");
        },
        update: (cache, { data }) => {
          if (!data?.updateEventFeedback) return;

          try {
            const customerId = encodeURIComponent(input.pk);

            const cachedData = cache.readQuery<GetEventsByCustomerResponse>({
              query: GET_EVENTS_BY_CUSTOMER,
              variables: { customerId },
            });

            if (!cachedData || !cachedData.events) {
              return;
            }

            const updatedEvents = {
              ...cachedData.events,
              data: cachedData.events.data.map(item => {
                if (item?.event?.id === input.sk) {
                  return {
                    ...item,
                    event: {
                      ...item.event,
                      feedback: input.feedback,
                      mitigatedRisk: input.mitigatedRisk,
                    },
                  };
                }

                return item;
              }),
            };

            cache.writeQuery({
              query: GET_EVENTS_BY_CUSTOMER,
              variables: { customerId },
              data: { events: updatedEvents },
              overwrite: true,
            });
          } catch (error) {
            console.error("Error updating cache:", error);

            errorNotification();
          }
        },
      });

      return response.data?.updateEventFeedback;
    } catch (err) {
      console.error("Error updating event feedback:", err);
    }
  };

  return {
    updateEventFeedback,
    loading,
    error,
    data: data?.updateEventFeedback,
  };
};
